<script>
import request from '@/services/request'
import { mapActions } from 'vuex'

export default {
    name: 'modal-program-group-account',

    data() {
        return {
            errors: {},
            group: null,
            loading: false,
            saving: false,
            deleting_id: null,
            accounts: [],
            available_accounts: [],

            form: {
                bank_account_company_id: '',
            },
        }
    },

    methods: {
        open(group) {
            this.group = group;

            this.errors = {};
            this.accounts = [];
            this.available_accounts = [];
            this.form.bank_account_company_id = '';

            this.loadAccounts();

            this.$modal.show('modalProgramGroupAccounts');
        },

        close() {
            this.errors = {};
            this.group = null;
            this.accounts = [];
            this.available_accounts = [];
            this.form.bank_account_company_id = '';

            this.$modal.hide('modalProgramGroupAccounts');
        },

        async loadAccounts() {
            if (!this.group || !this.group.id) {
                return;
            }

            this.loading = true;

            try {
                const { data } = await request.get(`/program-groups/${this.group.id}/bank-accounts`);

                this.accounts = data.related_accounts || [];
                this.available_accounts = data.available_accounts || [];
            } catch (error) {
                console.log(error);
                await this.errorNotification(error);
            } finally {
                this.loading = false;
            }
        },

        async addAccount() {
            this.errors = {};

            if (!this.form.bank_account_company_id) {
                this.$swal({
                    icon: 'warning',
                    title: 'Atención',
                    text: 'Selecciona una cuenta bancaria.',
                });

                return;
            }

            this.saving = true;

            try {
                const { data } = await request.post(`/program-groups/${this.group.id}/bank-accounts`, {
                    bank_account_company_id: this.form.bank_account_company_id,
                });

                this.successNotification(data.message);

                this.form.bank_account_company_id = '';

                await this.loadAccounts();

                Event.$emit('event-reload-program-relations');

            } catch (error) {
                this.errors = await this.errorNotification(error);
            } finally {
                this.saving = false;
            }
        },

        removeAccount(account) {
            this.$swal({
                title: '¿Quitar cuenta?',
                text: 'La cuenta dejará de estar disponible para este grupo.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, quitar',
                cancelButtonText: 'Cancelar'
            }).then(async (result) => {
                if (!result.isConfirmed) {
                    return;
                }

                this.deleting_id = account.id;

                try {
                    const { data } = await request.destroy(
                        `/program-groups/${this.group.id}/bank-accounts/${account.id}`
                    );

                    this.successNotification(data.message);

                    await this.loadAccounts();

                    Event.$emit('event-reload-program-relations');

                } catch (error) {
                    await this.errorNotification(error);
                } finally {
                    this.deleting_id = null;
                }
            });
        },

        accountLabel(account) {
            let company = account.company_name || '';
            let bank = account.bank_name || '';
            let number = account.account || account.clabe || '';

            return `${company} - ${bank} - ${number}`;
        },

        ...mapActions({
            errorNotification: 'alert/error',
            successNotification: 'alert/success'
        })
    },

    created() {
        Event.$on('open-group-accounts-modal', (group) => {
            this.open(group);
        });
    },

    beforeDestroy() {
        Event.$off('open-group-accounts-modal');
    }
}
</script>

<template>
    <modal name="modalProgramGroupAccounts" :width="'70%'" :height="'75%'" :click-to-close="false">

        <button type="button" class="btn btn-danger btn-sm btn-closed" @click="close">
            <i class="fa-solid fa-xmark"></i>
        </button>

        <div class="modal-header">
            <h5 class="modal-title">
                Cuentas del grupo
                <span v-if="group">
                    - {{ group.name }}
                </span>
            </h5>
        </div>

        <div class="modal-body">

            <div v-if="loading">
                <div class="text-center mt-5">
                    <i class="fa-solid fa-circle-notch fa-spin fa-3x mb-1"></i>
                    <h5>Cargando cuentas</h5>
                </div>
            </div>

            <div v-else>
                <div class="row align-items-end mb-3">

                    <div class="col-md-9">
                        <label>Cuenta bancaria</label>

                        <select class="form-control" v-model="form.bank_account_company_id">
                            <option value="">Seleccione una cuenta</option>

                            <option v-for="account in available_accounts" :key="account.id" :value="account.id">
                                {{ account.label || accountLabel(account) }}
                            </option>
                        </select>


                    </div>

                    <div class="col-md-3">
                        <button class="btn btn-outline-success btn-block mt-n10"
                            :disabled="saving || !form.bank_account_company_id" @click="addAccount">

                            <span v-if="saving">
                                <i class="fa-solid fa-circle-notch fa-spin"></i>
                                Guardando
                            </span>

                            <span v-else>
                                <i class="fa fa-plus"></i>
                                Agregar
                            </span>
                        </button>
                    </div>

                    <div class="col-md-12">
                        <div class="text-danger" v-if="errors.bank_account_company_id">
                            {{ errors.bank_account_company_id[0] }}
                        </div>

                        <small class="text-muted" v-if="available_accounts.length === 0">
                            No hay cuentas disponibles para agregar.
                        </small>
                    </div>

                </div>

                <hr>

                <div class="row">
                    <div class="col-md-12">

                        <div class="table-responsive">
                            <table class="table table-bordered text-center">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Empresa</th>
                                        <th>Banco</th>
                                        <th>Cuenta</th>
                                        <th>CLABE</th>
                                        <th>Opciones</th>
                                    </tr>
                                </thead>

                                <tbody v-if="accounts.length > 0">
                                    <tr v-for="account in accounts" :key="account.id">
                                        <td>{{ account.id }}</td>
                                        <td>{{ account.company_name }}</td>
                                        <td>{{ account.bank_name }}</td>
                                        <td>
                                            <span v-if="account.account">
                                                {{ account.account }}
                                            </span>
                                            <span v-else class="text-muted">
                                                Sin cuenta
                                            </span>
                                        </td>
                                        <td>
                                            <span v-if="account.clabe">
                                                {{ account.clabe }}
                                            </span>
                                            <span v-else class="text-muted">
                                                Sin CLABE
                                            </span>
                                        </td>
                                        <td>
                                            <button class="btn btn-sm btn-outline-danger"
                                                :disabled="deleting_id === account.id" @click="removeAccount(account)">

                                                <span v-if="deleting_id === account.id">
                                                    <i class="fa-solid fa-circle-notch fa-spin"></i>
                                                </span>

                                                <span v-else>
                                                    <i class="fa fa-trash"></i>
                                                    Quitar
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>

                                <tbody v-else>
                                    <tr>
                                        <td colspan="6">
                                            Sin cuentas relacionadas.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>

            </div>

        </div>

        <div class="modal-footer">
            <button class="btn btn-outline-warning float-left" @click="close">
                <i class="fas fa-undo-alt"></i>
                Cerrar
            </button>
        </div>

    </modal>
</template>

<style scoped>
.text-danger {
    font-size: 12px;
}

@media screen and (min-width: 768px) {
    .mt-n10 {
        margin-top: -35px;
    }
}
</style>